﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import type { PostSelfServiceRegistrationResponse } from "~jrad/Services/API/Account/PostSelfServiceRegistration";
import type {
    SelfServiceErrorState,
    SelfServiceRequestData,
    SelfServiceRequestDataClaims,
    SelfServiceViewState,
} from "./Types";
import { SelfServiceRegistrationPage } from "./Types";
import type { IntlContext } from "~base/Internationalization/IntlContext";
import type { DataGatheringStatus } from "~jrad/Services/API/Account/PostSelfServiceRegistrationAppStatus";
import type {
    SetResourceMapLoaded,
    SetResourcesLoaded,
} from "~base/Pages/Home/Main/Store";

//-------------------------------------------------------------------------------------
// State
//-------------------------------------------------------------------------------------

type SetFetchError = { type: "fetchError"; message: string };
type SetCurrentPage = {
    type: "setCurrentPage";
    page: SelfServiceRegistrationPage;
};
type SetRequestData = {
    type: "setRequestData";
    key: keyof Omit<SelfServiceRequestData, "claims" | "hasAcceptedToS">;
    value: string;
};
type SetRequestDataBool = {
    type: "setRequestData";
    key: keyof Pick<SelfServiceRequestData, "hasAcceptedToS">;
    value: boolean;
};
type SetRequestDataClaim = {
    type: "setRequestDataClaim";
    key: keyof SelfServiceRequestDataClaims;
    value: string;
};
type SetVerificationStatus = {
    type: "setVerificationStatus";
    status: DataGatheringStatus;
};
type SetResponseData = {
    type: "setResponseData";
    data: PostSelfServiceRegistrationResponse;
};
type WaitForTextCodesToLoad = { type: "waitForTextCodesToLoad" };

export type SelfServiceViewAction =
    | SetFetchError
    | SetCurrentPage
    | SetRequestData
    | SetRequestDataBool
    | SetRequestDataClaim
    | SetVerificationStatus
    | SetResponseData
    | WaitForTextCodesToLoad
    | SetResourcesLoaded
    | SetResourceMapLoaded;

export function reducer(
    state: SelfServiceViewState,
    action: SelfServiceViewAction,
    intl: IntlContext
): SelfServiceViewState {
    switch (action.type) {
        case "fetchError": {
            return {
                ...state,
                errorMessage: action.message,
            };
        }
        case "waitForTextCodesToLoad": {
            return {
                ...state,
                waitForTextCodesToLoad: true,
            };
        }
        case "setResourceMapLoaded": {
            return {
                ...state,
            };
        }
        case "setResourcesLoaded": {
            return {
                ...state,
                waitForTextCodesToLoad: false,
            };
        }
        case "setCurrentPage": {
            return {
                ...state,
                page: action.page,
                nextPageAfterLoading: action.page,
                errorMessage: "",
                requestData: {
                    ...state.requestData,
                    passwordConfirm:
                        action.page === SelfServiceRegistrationPage.SelectTenant
                            ? state.requestData.password
                            : null,
                },
            };
        }
        case "setRequestData": {
            return {
                ...state,
                requestData: {
                    ...state.requestData,
                    [action.key]: action.value,
                },
            };
        }
        case "setRequestDataClaim": {
            // Unset claim error validation to prevent confusion by it showing constantly

            const responseError = { ...state.responseError };

            if (!!responseError[action.key]) {
                responseError[action.key] = undefined;
            }

            return {
                ...state,
                requestData: {
                    ...state.requestData,
                    claims: {
                        ...state.requestData.claims,
                        [action.key]: action.value,
                    },
                },
                responseError: responseError,
            };
        }
        case "setVerificationStatus": {
            return {
                ...state,
                verificationStatus: action.status,
            };
        }
        case "setResponseData": {
            if (action.data.IsRegistered) {
                return {
                    ...state,
                    responseData: {
                        ...state.responseData,
                        IsRegistered: true,
                    },
                };
            }

            if (action.data.HasError === true) {
                const errorState: SelfServiceErrorState = {};
                let errorMessage = "";

                action.data.Errors.forEach((error) => {
                    if (error.Field == "generic") {
                        if (errorMessage.length > 0) {
                            errorMessage += "\n";
                        }

                        errorMessage += intl.formatText(
                            error.Message,
                            ...error.MessageArguments
                        );
                    } else {
                        errorState[error.Field] = intl.formatText(
                            error.Message,
                            ...error.MessageArguments
                        );
                    }
                });

                return {
                    ...state,
                    errorMessage: errorMessage,
                    responseHasError: true,
                    responseError: errorState,
                };
            } else {
                let nextPage = SelfServiceRegistrationPage.Account;

                switch (state.page) {
                    case SelfServiceRegistrationPage.Account: {
                        if (action.data.Tenants.length > 1) {
                            nextPage = SelfServiceRegistrationPage.SelectTenant;
                        } else {
                            nextPage =
                                SelfServiceRegistrationPage.TermsOfService;
                        }
                        break;
                    }
                    case SelfServiceRegistrationPage.SelectTenant: {
                        nextPage = SelfServiceRegistrationPage.TermsOfService;
                        break;
                    }
                    case SelfServiceRegistrationPage.AppRegistration:
                    case SelfServiceRegistrationPage.TermsOfService: {
                        if (state.responseData.AllowAppRegistration) {
                            nextPage =
                                SelfServiceRegistrationPage.AppRegistration;
                        } else {
                            nextPage = SelfServiceRegistrationPage.EnterClaims;
                        }
                        break;
                    }
                }

                return {
                    ...state,
                    errorMessage: "",
                    responseError: {},
                    responseHasError: false,
                    responseData: action.data,
                    nextPageAfterLoading: nextPage,
                };
            }
        }
    }
}

// ----------------------------------------------------------------------------------------------------
// Initial state
// ----------------------------------------------------------------------------------------------------

export const initialState: SelfServiceViewState = {
    page: SelfServiceRegistrationPage.Account,
    nextPageAfterLoading: SelfServiceRegistrationPage.Account,

    requestData: {
        claims: {},
        hasAcceptedToS: false,
    },
    responseHasError: false,
    responseData: {
        AllowAppRegistration: false,
        AllowManualRegistration: false,
        AgreementLink: null,
        AgreementText: null,
        IsRegistered: false,
        Tenants: [],
    },
    responseError: {},
};
